import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ProCard } from '../components/obros/ProCard';
import { JobCard } from '../components/jobs/JobCard';
import { CategoryChip } from '../components/obros/CategoryChip';
import { IconButton } from '../components/obros/IconButton';
import { Logo } from '../components/obros/Logo';
import { Button } from '../components/ui/Button';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton';
import { categoriesApi, jobsApi, usersApi } from '../services/api';
import type { Category, Job, User } from '../types';
import { useAuthStore } from '../stores/authStore';

export const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) return <GuestWelcome />;
  return user?.user_type === 'worker' ? <WorkerHome /> : <ClientHome />;
};

// Calca ScreenWelcome de screens-auth.jsx (Claude Design): logo + headline centrados,
// un solo CTA a "Crear cuenta" (el tipo cliente/profesional se elige en el paso 1 del registro).
const GuestWelcome: React.FC = () => (
  <div className="min-h-full bg-cream2 flex flex-col px-6 pt-10 pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
    <div className="flex-1 flex flex-col items-center justify-center gap-8">
      <Logo className="h-11" />

      <div className="text-center max-w-[280px]">
        <h1 className="text-[36px] font-extrabold tracking-tight text-onSurface leading-[1.05] mb-3">
          Encontrá manos
          <br />
          <span className="text-primary-700">expertas</span> cerca tuyo
        </h1>
        <p className="text-[15px] text-onSurface-var leading-relaxed">
          Albañiles, plomeros, electricistas y más. Publicá tu trabajo o postulate a uno.
        </p>
      </div>

      <div className="flex gap-1.5">
        <span className="w-6 h-1.5 rounded-full bg-primary-500" />
        <span className="w-1.5 h-1.5 rounded-full bg-outlineWarm" />
        <span className="w-1.5 h-1.5 rounded-full bg-outlineWarm" />
      </div>
    </div>

    <div className="max-w-sm w-full mx-auto flex flex-col gap-3">
      <Link to="/register">
        <Button variant="filled" pill size="xl" className="w-full justify-center">
          Crear cuenta
        </Button>
      </Link>
      <Link to="/login">
        <Button variant="text" size="md" className="w-full justify-center">
          Ya tengo cuenta · Iniciar sesión
        </Button>
      </Link>
    </div>
  </div>
);

// Calca HomeHeader de screens-home.jsx (Claude Design)
const HomeHeader: React.FC<{ searchTo: string }> = ({ searchTo }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(search ? `${searchTo}?search=${encodeURIComponent(search)}` : searchTo);
  };

  return (
    <div className="px-4 pt-2 pb-4 bg-cream2">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[13px] font-medium text-onSurface-var inline-flex items-center gap-1">
            <MaterialIcon name="location_on" size={14} className="text-primary-700" />
            {user?.city || 'Ubicación no especificada'}
          </p>
          <h1 className="text-[22px] font-extrabold tracking-tight text-onSurface mt-0.5">Hola, {user?.first_name} 👋</h1>
        </div>
        <div className="flex gap-1">
          <IconButton icon="forum" onClick={() => navigate('/messages')} />
          <IconButton icon="notifications" />
        </div>
      </div>

      <form onSubmit={submit} className="h-[52px] rounded-full bg-surface border border-outlineVarWarm flex items-center pl-[18px] pr-2">
        <MaterialIcon name="search" size={20} className="text-onSurface-mute mr-2.5" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar trabajos, profesionales..."
          className="flex-1 bg-transparent outline-none text-sm text-onSurface placeholder-onSurface-mute"
        />
        <button type="button" onClick={() => navigate(searchTo)} className="w-9 h-9 rounded-full bg-primary-500 text-white flex items-center justify-center shrink-0">
          <MaterialIcon name="tune" size={18} />
        </button>
      </form>
    </div>
  );
};

const CategoryStrip: React.FC<{ categories: Category[]; loading: boolean; active: number | null; onChange: (id: number | null) => void }> = ({
  categories,
  loading,
  active,
  onChange,
}) => (
  <div className="flex gap-2 px-4 py-3.5 overflow-x-auto scrollbar-hide bg-cream2">
    {loading ? (
      Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 w-24 rounded-full shrink-0" />)
    ) : (
      <>
        <CategoryChip label="Todos" icon="apps" active={active === null} onClick={() => onChange(null)} />
        {categories.map((cat) => (
          <CategoryChip key={cat.id} label={cat.name} icon={cat.icon} active={active === cat.id} onClick={() => onChange(cat.id)} />
        ))}
      </>
    )}
  </div>
);

// Banner promocional (solo cliente) — calca el "hero" de ScreenHome en screens-home.jsx
const PromoBanner: React.FC = () => (
  <div className="px-4 pb-4">
    <Link
      to="/my-jobs"
      className="relative flex items-center gap-3 overflow-hidden rounded-[20px] bg-gradient-to-br from-onSurface to-[#2a2018] text-cream2 p-4"
    >
      <div className="flex-1">
        <p className="text-[11px] font-bold tracking-wide text-primary-400 mb-1">NUEVO TRABAJO</p>
        <p className="text-base font-bold leading-tight mb-0.5">Publicá lo que necesitás</p>
        <p className="text-xs text-white/70">Recibí propuestas en minutos</p>
      </div>
      <span className="w-11 h-11 rounded-full bg-primary-500 text-white flex items-center justify-center shrink-0">
        <MaterialIcon name="arrow_forward" size={22} />
      </span>
      <MaterialIcon name="construction" size={140} className="absolute -right-7 -bottom-7 text-white/5" />
    </Link>
  </div>
);

const SectionTitle: React.FC<{ title: string; to?: string }> = ({ title, to }) => (
  <div className="flex items-baseline justify-between px-4 mb-3">
    <h2 className="text-[17px] font-bold text-onSurface">{title}</h2>
    {to && (
      <Link to={to} className="text-[13px] font-semibold text-primary-700 hover:text-primary-800">
        Ver todos
      </Link>
    )}
  </div>
);

const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-4 rounded-2xl border border-dashed border-outlineWarm bg-surface/60 px-5 py-8 text-center text-sm text-onSurface-mute">
    {children}
  </div>
);

const Fab: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <Link
    to={to}
    // bottom: altura del BottomNav (~4.5rem) + el safe-area del home indicator de iOS + aire,
    // si no el FAB queda pegado o tapado por la barra inferior en iPhones con notch/isla.
    className="fixed z-30 right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom)+0.75rem)] lg:bottom-6 h-14 pl-[18px] pr-5 rounded-2xl bg-primary-500 text-white shadow-lg shadow-primary-500/30 inline-flex items-center gap-2.5 font-semibold text-[15px] hover:bg-primary-600 transition-colors"
  >
    <MaterialIcon name="add" size={24} />
    {label}
  </Link>
);

// Calca el branch "cliente" de ScreenHome en screens-home.jsx.
const ClientHome: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [workers, setWorkers] = useState<User[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    categoriesApi.list({}).then((res) => setCategories(res.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    usersApi
      .workers({ category_id: activeCategory ?? undefined, per_page: 8 })
      .then((res) => setWorkers(res.data.data ?? res.data))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <div className="bg-cream2 min-h-full pb-24">
      <div className="max-w-xl mx-auto">
        <HomeHeader searchTo="/workers" />
        <CategoryStrip categories={categories} loading={categories.length === 0} active={activeCategory} onChange={setActiveCategory} />
        <PromoBanner />

        <SectionTitle title="Cerca tuyo" to="/workers" />
        <div className="flex flex-col gap-3 px-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
          ) : workers.length === 0 ? (
            <EmptyHint>No encontramos profesionales para esta categoría.</EmptyHint>
          ) : (
            workers.map((w) => <ProCard key={w.id} worker={w} />)
          )}
        </div>
      </div>

      <Fab to="/my-jobs" label="Publicar trabajo" />
    </div>
  );
};

// Calca el branch "profesional" de ScreenHome en screens-home.jsx.
const WorkerHome: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    categoriesApi.list({}).then((res) => setCategories(res.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    jobsApi
      .list({ category_id: activeCategory ?? undefined, per_page: 10 })
      .then((res) => setJobs(res.data.data))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <div className="bg-cream2 min-h-full pb-24">
      <div className="max-w-xl mx-auto">
        <HomeHeader searchTo="/worker/jobs" />
        <CategoryStrip categories={categories} loading={categories.length === 0} active={activeCategory} onChange={setActiveCategory} />

        <SectionTitle title="Trabajos disponibles" to="/worker/jobs" />
        <div className="flex flex-col gap-3 px-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
          ) : jobs.length === 0 ? (
            <EmptyHint>No hay trabajos disponibles para esta categoría todavía.</EmptyHint>
          ) : (
            jobs.map((job) => <JobCard key={job.id} job={job} />)
          )}
        </div>
      </div>
    </div>
  );
};
